import { Alert, Button, Drawer, Space, Table, Typography } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import { useRef, useState } from "react";
import { Client } from "../../../core/api-client";
import { API_PREFIX_V1 } from "../../../shared/constants";

const REQUIRED = ["ENTITY", "RECEIVED_DATE", "CLIENT", "PROJECT_TYPE", "OPPORTUNITY", "LEAD_OWNER_EMPLOYEE_ID", "DELIVERY_OWNER_EMPLOYEE_ID", "LOST_DATE", "DEAL_STAGE", "DEAL_CURRENCY", "DEAL_VALUE", "TARGET_CURRENCY"];
const COLUMNS = [...REQUIRED, "CLIENT_LOCATION","CLIENT_CATEGORY", "DEAL_SUB_STAGE", "GROSS_MARGIN", "QUOTATION_SOW_NUMBER", "NEW_RENEWAL","REMARKS"];
const TEMPLATE_HEADERS: Record<string, string> = {
  RECEIVED_DATE: "SALE_RECEIVED_DATE",
  PROJECT_TYPE: "OPPORTUNITY_TYPE",
  TARGET_CURRENCY: "REPORTING_CURRENCY",
  GROSS_MARGIN: "GROSS_MARGIN_PERCENT",
  OPPORTUNITY:"OPPORTUNITY_DESCRIPTION"
  
};
const normalizeHeader = (header: string) => {
  const normalized = header.replace(/^\uFEFF/, "").trim().toUpperCase();
  return Object.keys(TEMPLATE_HEADERS).find((key) => TEMPLATE_HEADERS[key] === normalized) || normalized;
};
const displayField = (field: string) => TEMPLATE_HEADERS[field] || field;
const download = (name: string, csv: string) => {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url; link.download = name; link.click(); URL.revokeObjectURL(url);
};

export default function SalesBulkUpload({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<any>(null);
  const batch = useRef("");
  const parseFile = async (file?: File) => {
    setRows([]); setResult(null); setMessage(""); setFilename(file?.name || "");
    if (!file) return;
    if (!/\.csv$/i.test(file.name) || file.size > 5 * 1024 * 1024) {
      setMessage("Select a CSV file up to 5 MB"); return;
    }
    setBusy(true);
    try {
      const parsed = Papa.parse<Record<string, string>>(await file.text(), {
        header: true, skipEmptyLines: "greedy", transformHeader: normalizeHeader,
      });
      const headers = parsed.meta.fields || [];
      const missing = REQUIRED.filter((column) => !headers.includes(column));
      const unknown = headers.filter((column) => !COLUMNS.includes(column));
      if (missing.length) throw new Error(`Missing columns: ${missing.map(displayField).join(", ")}`);
      if (unknown.length || new Set(headers).size !== headers.length) throw new Error("Unknown or duplicate CSV columns");
      if (parsed.errors.length) throw new Error(`CSV error: ${parsed.errors[0].message}`);
      if (!parsed.data.length || parsed.data.length > 500) throw new Error("Upload between 1 and 500 rows");
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
      batch.current = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      setRows(parsed.data);
    } catch (error: any) { setMessage(error.message || "Unable to read CSV"); }
    finally { setBusy(false); }
  };
  const submit = async (importing: boolean) => {
    if (busy) return;
    setBusy(true); setMessage("");
    try {
      const response = await Client.post(`${API_PREFIX_V1}sales/${importing ? "bulk-import" : "bulk-validate"}`,
        importing ? { rows, batchId: batch.current } : { rows }, { shouldHide: true });
      if (!response || response.status < 200 || response.status >= 300) throw response?.data;
      setResult(response.data);
      if (importing && response.data.valid) onImported();
    } catch (error: any) {
      setMessage(Array.isArray(error?.message) ? error.message.join("; ") : error?.message || "Request failed. Retry with the same file.");
    } finally { setBusy(false); }
  };
  const complete = result?.importedCount > 0;
  return <Drawer title="Bulk Upload Sales" open maskClosable={false} closable={!busy} keyboard={!busy} onClose={onClose}
    width={960} contentWrapperStyle={{ maxWidth: "100vw" }} className="form-drawer" footer={<Space wrap>
      <Button disabled={busy} onClick={onClose}>{complete ? "Done" : "Cancel"}</Button>
      {!complete && <Button disabled={!rows.length || busy} loading={busy && !result?.valid} onClick={() => submit(false)}>Validate</Button>}
      {!complete && <Button type="primary" loading={busy && result?.valid} disabled={!result?.valid || busy} onClick={() => submit(true)}>Import {rows.length || ""}</Button>}
    </Space>}>
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {!complete && <Space wrap>
        <Button icon={<DownloadOutlined />} disabled={busy} onClick={() => download("sales-upload-template.csv", Papa.unparse({ fields: COLUMNS.map(displayField), data: [] }))}>Download Template</Button>
        <Button icon={<UploadOutlined />} disabled={busy} onClick={() => input.current?.click()}>Select CSV</Button>
        <input ref={input} type="file" accept=".csv,text/csv" aria-label="Sales CSV file" hidden disabled={busy}
          onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; parseFile(file); }} />
        <Typography.Text style={{ overflowWrap: "anywhere" }}>{filename}{rows.length ? ` (${rows.length} rows)` : ""}</Typography.Text>
      </Space>}
      {message && <Alert type="error" showIcon message={message} />}
      {complete && <Alert type="success" showIcon message={`${result.importedCount} sales records imported`} />}
      {result?.errors?.length > 0 && <>
        <Alert type="error" showIcon message={`${result.errors.length} validation errors. No records imported.`} />
        <Button icon={<DownloadOutlined />} onClick={() => download("sales-upload-errors.csv", Papa.unparse(result.errors.map((error: any) => ({ ...error, field: displayField(error.field) })), { escapeFormulae: true }))}>Download Errors</Button>
        <Table size="small" rowKey={(_, index) => String(index)} dataSource={result.errors} scroll={{ x: 520 }}
          columns={[{ title: "Row", dataIndex: "row", width: 65 }, { title: "Field", dataIndex: "field", width: 210, render: displayField }, { title: "Error", dataIndex: "message" }]} />
      </>}
      {result?.valid && !complete && <Alert type="success" showIcon message={`${rows.length} rows ready to import`} />}
      {result?.preview?.length > 0 && !complete && <Table size="small" rowKey="row" dataSource={result.preview} scroll={{ x: 880 }} columns={[
        { title: "Row", dataIndex: "row" }, { title: "Entity", dataIndex: "entity" }, { title: "Client", dataIndex: "client" },
        { title: "Opportunity", dataIndex: "opportunity" }, { title: "Deal Currency", dataIndex: "dealCurrency" },
        { title: "Deal Value", dataIndex: "dealValue" }, { title: "Reporting Currency", dataIndex: "targetCurrency" },
        { title: "Deal Value (Reporting)", dataIndex: "targetValue" }, 
        // { title: "Closure Qtr", dataIndex: "closureQuarter" },
      ]} />}
      {complete && <Table size="small" rowKey="row" dataSource={result.references} columns={[{ title: "Row", dataIndex: "row" }, { title: "Deal Reference", dataIndex: "dealReference" }]} />}
    </Space>
  </Drawer>;
}
